import { useCallback, useRef, useState } from "react";

const PREVIEW_UNAVAILABLE_ERROR =
  "Handwritten card preview is temporarily unavailable. Please try again later.";

// `fetchSignedUrl` must resolve to `{ res, err }` where `res.data.signed_url` is the iframe URL.
const useHandwrittenCardPreview = () => {
  const [title, setTitle] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const [error, setError] = useState(null);
  const latestRequestRef = useRef(0);

  const closePreview = useCallback(() => {
    latestRequestRef.current += 1;
    setIsLoading(false);
    setSignedUrl(null);
    setError(null);
  }, []);

  const openPreview = useCallback(async (previewTitle, fetchSignedUrl) => {
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;

    setTitle(previewTitle);
    setIsLoading(true);
    setSignedUrl(null);
    setError(null);

    const { res, err } = await fetchSignedUrl();

    // The modal was closed (or another preview opened) while this request was in flight
    if (requestId !== latestRequestRef.current) return;

    const url = res?.data?.signed_url;
    if (err || !url) {
      setError(err?.response?.data?.error || PREVIEW_UNAVAILABLE_ERROR);
    } else {
      setSignedUrl(url);
    }
    setIsLoading(false);
  }, []);

  return {
    title,
    isLoading,
    signedUrl,
    error,
    isPreviewOpen: isLoading || !!signedUrl || !!error,
    openPreview,
    closePreview,
  };
};

export default useHandwrittenCardPreview;
